import React from 'react';
import { useNavigate } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Stack, Typography } from '@mui/material';

import CustomButton from 'components/button/CustomButton';
import LabelChip from 'components/chip/LabelChip';
import Circular from 'components/common/CustomCircular';
import { Condition, Filter } from 'components/condition-list/SearchFilter';
import {
  ConsoleColumn,
  ConsoleDetailDrawer,
  ConsoleHeader,
  ConsoleSearchFilter,
  ConsoleTable,
  useQueryInitialFilters,
} from 'components/console';
import DeleteConfirmDialog from 'components/dialog/DeleteConfirmDialog';
import { MSG_MIN_VALUE, MSG_REQUIRED } from 'constant/ValidationMessages';
import {
  ExpenseLabel,
  ExpenseListItem,
  ExpenseStore,
  ListExpenseParams,
  createExpense,
  deleteExpense,
  getExpense,
  listExpenseLabels,
  listExpenseStores,
  listExpenses,
  updateExpense,
} from 'stores/ExpenseStoreAdapter';
import { formatDate } from 'utils/DateTimeFormatter';
import { errorDefault, successDefault, toErrorDetail } from 'utils/Toastify';

import ExpenseForm, { ExpenseFormErrors, ExpenseFormValues } from './components/ExpenseForm';

const NAME_MAX = 128;
const NOTE_MAX = 1024;

type DrawerMode = 'none' | 'create' | 'detail';

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const emptyForm = (): ExpenseFormValues => ({
  purchasedOn: todayStr(),
  name: '',
  amount: '',
  quantity: '1',
  storeId: '',
  labelId: '',
  note: '',
});

const yen = (n: number) => `${n.toLocaleString('ja-JP')} 円`;

/**
 * 経費一覧・登録/編集（パターンB・コンソール）。
 * ConsoleHeader + ConsoleTable（多列）+ ConsoleDetailDrawer（登録/編集）。
 * ダッシュボードからのドリルダウン（labelIds/storeIds/dateFrom/dateTo）をクエリで初期条件にする。
 */
/** 検索フィルタ（Filter[]）を経費一覧APIのパラメータへ変換する。 */
const filtersToParams = (filters: Filter[]): ListExpenseParams => {
  const params: ListExpenseParams = {};
  filters.forEach((f) => {
    const raw = f.value.key;
    if (!raw) return;
    switch (f.key) {
      case 'keyword':
        params.keyword = raw;
        break;
      case 'dateFrom':
        // date-time-picker-start は yyyy-MM-dd'T'HH:mm:ss を返すため日付部分に丸める
        params.dateFrom = raw.substring(0, 10);
        break;
      case 'dateTo':
        params.dateTo = raw.substring(0, 10);
        break;
      case 'labelIds':
        params.labelIds = raw.split(',').filter(Boolean);
        break;
      case 'storeIds':
        params.storeIds = raw.split(',').filter(Boolean);
        break;
      default:
        break;
    }
  });
  return params;
};

const ExpenseManagement: React.FC = () => {
  const navigate = useNavigate();

  const [expenses, setExpenses] = React.useState<ExpenseListItem[]>([]);
  const [stores, setStores] = React.useState<ExpenseStore[]>([]);
  const [labels, setLabels] = React.useState<ExpenseLabel[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  // 現在適用中の検索パラメータ（作成/更新/削除・更新ボタンで同条件を再検索するため保持）
  const [activeParams, setActiveParams] = React.useState<ListExpenseParams>({});

  const [drawerMode, setDrawerMode] = React.useState<DrawerMode>('none');
  const [selected, setSelected] = React.useState<ExpenseListItem | null>(null);
  const [isEditing, setIsEditing] = React.useState(false);
  const [form, setForm] = React.useState<ExpenseFormValues>(emptyForm());
  const [errors, setErrors] = React.useState<ExpenseFormErrors>({});
  const [deleteOpen, setDeleteOpen] = React.useState(false);

  // 検索条件定義（他画面と同じ SearchCondition 化・保存/デフォルト★/user_profile 対応）
  const searchConditions: Condition[] = React.useMemo(
    () => [
      { key: 'keyword', name: '経費名', type: 'text', color: '#ef6c00' },
      { key: 'dateFrom', name: '購入日（From）', type: 'date-time-picker-start', color: '#fb8c00' },
      { key: 'dateTo', name: '購入日（To）', type: 'date-time-picker-end', color: '#fb8c00' },
      {
        key: 'labelIds',
        name: '品目ラベル',
        type: 'select-multi',
        color: '#6a1b9a',
        values: labels.map((l) => ({ key: l.expenseLabelId, name: l.name })),
      },
      {
        key: 'storeIds',
        name: '店舗',
        type: 'select-multi',
        color: '#1565c0',
        values: stores.map((s) => ({ key: s.expenseStoreId, name: s.name })),
      },
    ],
    [labels, stores],
  );

  const fetchExpenses = React.useCallback(async (params: ListExpenseParams) => {
    setLoading(true);
    try {
      const list = await listExpenses(params);
      setExpenses(list);
    } catch (e) {
      errorDefault('経費の取得に失敗しました', toErrorDetail(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMasters = React.useCallback(async () => {
    const [storeList, labelList] = await Promise.all([
      listExpenseStores().catch((): ExpenseStore[] => []),
      listExpenseLabels().catch((): ExpenseLabel[] => []),
    ]);
    setStores(storeList);
    setLabels(labelList);
  }, []);

  React.useEffect(() => {
    fetchMasters();
  }, [fetchMasters]);

  // URL クエリパラメータ（ドリルダウン）を初期検索条件として反映する
  const { queryParams, initialFilters } = useQueryInitialFilters(searchConditions);
  React.useEffect(() => {
    const params = filtersToParams(initialFilters);
    setActiveParams(params);
    fetchExpenses(params);
    // クエリ由来の初期条件が変わったときのみ再取得する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  // 検索実行（フィルタ変更・保存条件呼び出し・デフォルト★）のたびに一覧を再取得する
  const handleSearch = (filters: Filter[]) => {
    closeDrawer();
    const params = filtersToParams(filters);
    setActiveParams(params);
    fetchExpenses(params);
  };

  const refetch = () => fetchExpenses(activeParams);

  const closeDrawer = () => {
    setDrawerMode('none');
    setSelected(null);
    setIsEditing(false);
    setErrors({});
  };

  const handleStoreCreated = (store: ExpenseStore) => setStores((prev) => [...prev, store]);
  const handleLabelCreated = (label: ExpenseLabel) => setLabels((prev) => [...prev, label]);

  const handleCreateClick = () => {
    setSelected(null);
    setForm(emptyForm());
    setErrors({});
    setIsEditing(false);
    setDrawerMode('create');
  };

  const handleRowClick = async (row: ExpenseListItem) => {
    setSelected(row);
    setDrawerMode('detail');
    setIsEditing(false);
    setErrors({});
    try {
      const detail = await getExpense(row.expenseId);
      setForm({
        purchasedOn: detail.purchasedOn,
        name: detail.name,
        amount: String(detail.amount),
        quantity: String(detail.quantity),
        storeId: detail.storeId,
        labelId: detail.labelId,
        note: detail.note ?? '',
      });
    } catch (e) {
      const status = (e as { status?: number })?.status;
      if (status === 403 || status === 404) {
        errorDefault('対象の経費が見つかりません', toErrorDetail(e));
        closeDrawer();
        refetch();
        return;
      }
      errorDefault('経費の取得に失敗しました', toErrorDetail(e));
    }
  };

  const validate = (): boolean => {
    const e: ExpenseFormErrors = {};
    if (!form.purchasedOn) e.purchasedOn = MSG_REQUIRED('購入日');
    if (!form.name.trim()) e.name = MSG_REQUIRED('経費名');
    else if (form.name.length > NAME_MAX)
      e.name = `経費名は${NAME_MAX}文字以内で入力してください。`;

    if (form.amount === '') e.amount = MSG_REQUIRED('金額');
    else if (!/^\d+$/.test(form.amount)) e.amount = MSG_MIN_VALUE('金額', 0);

    if (form.quantity === '') e.quantity = MSG_REQUIRED('数量');
    else if (!/^\d+$/.test(form.quantity) || Number(form.quantity) < 1)
      e.quantity = MSG_MIN_VALUE('数量', 1);

    if (!form.storeId) e.storeId = MSG_REQUIRED('店舗');
    if (!form.labelId) e.labelId = MSG_REQUIRED('品目ラベル');
    if (form.note && form.note.length > NOTE_MAX)
      e.note = `メモは${NOTE_MAX}文字以内で入力してください。`;

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildParams = () => ({
    purchasedOn: form.purchasedOn,
    name: form.name.trim(),
    amount: Number(form.amount),
    quantity: Number(form.quantity),
    storeId: form.storeId,
    labelId: form.labelId,
    ...(form.note.trim() ? { note: form.note.trim() } : {}),
  });

  /** 保存時のエラー→UXマッピング（API4131/4132 準拠）。 */
  const handleSaveError = (e: unknown, fallbackMsg: string) => {
    const status = (e as { status?: number })?.status;
    if (status === 400) {
      errorDefault('店舗または品目ラベルが不正です。マスタを再取得します', toErrorDetail(e));
      fetchMasters();
      return;
    }
    if (status === 403 || status === 404) {
      errorDefault('対象の経費が見つかりません', toErrorDetail(e));
      closeDrawer();
      refetch();
      return;
    }
    errorDefault(fallbackMsg, toErrorDetail(e));
  };

  const handleCreate = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await createExpense(buildParams());
      successDefault('経費を登録しました');
      closeDrawer();
      refetch();
    } catch (e) {
      handleSaveError(e, '経費の登録に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selected || !validate()) return;
    setSaving(true);
    try {
      await updateExpense({ expenseId: selected.expenseId, ...buildParams() });
      successDefault('経費を更新しました');
      closeDrawer();
      refetch();
    } catch (e) {
      handleSaveError(e, '経費の更新に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await deleteExpense({ expenseId: selected.expenseId });
      successDefault('経費を削除しました');
      setDeleteOpen(false);
      closeDrawer();
      refetch();
    } catch (e) {
      setDeleteOpen(false);
      const status = (e as { status?: number })?.status;
      if (status === 403 || status === 404) {
        errorDefault('対象の経費が見つかりません', toErrorDetail(e));
        closeDrawer();
        refetch();
      } else {
        errorDefault('経費の削除に失敗しました', toErrorDetail(e));
      }
    } finally {
      setSaving(false);
    }
  };

  const columns: ConsoleColumn<ExpenseListItem>[] = [
    {
      key: 'purchasedOn',
      header: '購入日',
      width: 110,
      render: (e) => (
        <Box component="span" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {formatDate(e.purchasedOn)}
        </Box>
      ),
    },
    { key: 'name', header: '経費名', width: 220, ellipsis: (e) => e.name },
    {
      key: 'amount',
      header: '金額（単価）',
      width: 110,
      align: 'right',
      render: (e) => <Box component="span">{yen(e.amount)}</Box>,
    },
    {
      key: 'quantity',
      header: '数量',
      width: 70,
      align: 'right',
      render: (e) => <Box component="span">{e.quantity}</Box>,
    },
    {
      key: 'total',
      header: '合計',
      width: 120,
      align: 'right',
      render: (e) => (
        <Box component="span" sx={{ fontWeight: 700 }}>
          {yen(e.total)}
        </Box>
      ),
    },
    { key: 'storeName', header: '店舗', width: 150, ellipsis: (e) => e.storeName },
    {
      key: 'label',
      header: '品目ラベル',
      width: 130,
      render: (e) => <LabelChip name={e.labelName} color={e.labelColor} size="small" />,
    },
  ];

  const drawerFooter =
    drawerMode === 'create' ? (
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <CustomButton label="キャンセル" buttonVariant="secondary" onClick={closeDrawer} />
        <CustomButton
          label="登録"
          buttonVariant="primary"
          icon={<AddIcon />}
          onClick={handleCreate}
        />
      </Stack>
    ) : selected && isEditing ? (
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <CustomButton
          label="キャンセル"
          buttonVariant="secondary"
          onClick={() => setIsEditing(false)}
        />
        <CustomButton
          label="更新"
          buttonVariant="primary"
          icon={<SaveIcon />}
          onClick={handleUpdate}
        />
      </Stack>
    ) : selected ? (
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <CustomButton
          label="削除"
          buttonVariant="danger"
          icon={<DeleteIcon />}
          onClick={() => setDeleteOpen(true)}
        />
        <CustomButton
          label="編集"
          buttonVariant="primary"
          icon={<EditIcon />}
          onClick={() => setIsEditing(true)}
        />
      </Stack>
    ) : undefined;

  return (
    <Box sx={{ height: '100%', overflow: 'hidden' }}>
      <ConsoleHeader
        title="経費"
        count={expenses.length}
        description="購入品の登録・編集・検索を行います"
        backLabel="経費管理"
        onBack={() => navigate('/expense')}
        onRefresh={refetch}
        actions={
          <CustomButton
            label="経費を登録"
            buttonVariant="primary"
            icon={<AddIcon />}
            onClick={handleCreateClick}
          />
        }
      />

      <ConsoleTable
        columns={columns}
        rows={expenses}
        rowKey={(e) => e.expenseId}
        selectedKey={drawerMode === 'detail' ? selected?.expenseId : undefined}
        onRowClick={handleRowClick}
        emptyMessage="経費がありません。「経費を登録」から登録してください。"
        toolbar={
          <ConsoleSearchFilter
            conditions={searchConditions}
            initialFilters={initialFilters}
            onFiltersChange={handleSearch}
            conditionKey="expense-list"
            userProfileKey="expense-list-search"
          />
        }
      />

      <ConsoleDetailDrawer
        open={drawerMode === 'create'}
        onClose={closeDrawer}
        title="経費の登録"
        width={640}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ExpenseForm
              values={form}
              onChange={setForm}
              stores={stores}
              labels={labels}
              onStoreCreated={handleStoreCreated}
              onLabelCreated={handleLabelCreated}
              errors={errors}
            />
          </Box>
          <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider', mt: 2 }}>{drawerFooter}</Box>
        </Box>
      </ConsoleDetailDrawer>

      <ConsoleDetailDrawer
        open={drawerMode === 'detail' && selected !== null}
        onClose={closeDrawer}
        title={selected?.name ?? ''}
        badges={selected && <LabelChip name={selected.labelName} color={selected.labelColor} />}
        width={640}
      >
        {selected && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <ExpenseForm
                values={form}
                onChange={setForm}
                stores={stores}
                labels={labels}
                onStoreCreated={handleStoreCreated}
                onLabelCreated={handleLabelCreated}
                errors={isEditing ? errors : {}}
                disabled={!isEditing}
              />
            </Box>
            {drawerFooter && (
              <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider', mt: 2 }}>{drawerFooter}</Box>
            )}
          </Box>
        )}
      </ConsoleDetailDrawer>

      <DeleteConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        title="経費の削除"
        confirmLabel="削除"
        alertConfirmText="削除"
      >
        <Typography variant="body2">
          「{selected?.name}」を削除します。この操作は取り消せません（物理削除）。
        </Typography>
      </DeleteConfirmDialog>

      <Circular open={loading || saving} />
    </Box>
  );
};

export default ExpenseManagement;
